import { type HTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './Badges.css';

export interface LevelBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  level: number;
  size?: 'sm' | 'md' | 'lg';
  /** Override the icon. Defaults to a shield. */
  icon?: ReactNode;
  /** Short label shown before the number (e.g. "დონე"). Omit for number-only. */
  label?: string;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * LevelBadge — green shield pill showing the user's current level. Lives next
 * to the XPBadge on the home top bar and on the profile progress card. Shares
 * the badge-family physics; "level" is its own green data-kind.
 */
export function LevelBadge({ level, size = 'md', icon, label, className, ref, ...rest }: LevelBadgeProps) {
  return (
    <span
      ref={ref}
      className={clsx('fluent-badge', className)}
      data-kind="level"
      data-size={size}
      aria-label={label ? `${label} ${level}` : `Level ${level}`}
      {...rest}
    >
      <span className="fluent-badge__icon" aria-hidden="true">
        {icon ?? <ShieldIcon />}
      </span>
      <span className="fluent-badge__value">
        {label ? `${label} ${level}` : level}
      </span>
    </span>
  );
}

const SHIELD_PATH = 'M12 2.4l7.6 2.85v5.75c0 4.35-2.95 8.15-7.6 8.75-4.65-.6-7.6-4.4-7.6-8.75V5.25L12 2.4z';
const STAR_PATH = 'M12 7l1 2.63 2.8.13-2.18 1.77.73 2.71L12 12.7l-2.35 1.54.73-2.71-2.18-1.77 2.8-.13L12 7z';

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="fluent-level-shield-face" x1="12" y1="2.4" x2="12" y2="19.75" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffe487" />
          <stop offset="0.55" stopColor="#ffb938" />
          <stop offset="1" stopColor="#f5922b" />
        </linearGradient>
        <linearGradient id="fluent-level-star" x1="12" y1="7" x2="12" y2="14.2" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#fff4d6" />
        </linearGradient>
      </defs>
      {/* base / thickness — darker copy offset down gives the 3D bottom edge */}
      <path d={SHIELD_PATH} fill="rgba(120,60,0,0.45)" transform="translate(0 1.45)" />
      {/* gold gradient face */}
      <path d={SHIELD_PATH} fill="url(#fluent-level-shield-face)" stroke="#e07b1a" strokeWidth="0.7" />
      {/* glossy highlight on the upper-left */}
      <ellipse cx="8.8" cy="6.8" rx="1.5" ry="2.2" fill="rgba(255,255,255,0.55)" transform="rotate(-22 8.8 6.8)" />
      {/* white star emblem */}
      <path d={STAR_PATH} fill="url(#fluent-level-star)" stroke="#f0a830" strokeWidth="0.4" strokeLinejoin="round" />
    </svg>
  );
}
